import {
  createFileRoute,
  Link,
  useParams,
  useNavigate,
} from "@tanstack/react-router";
import { useState, useEffect, useRef, memo } from "react";
import { getSession } from "@/lib/session";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { runtimeConfig } from "@/lib/runtime-config";
import { toast } from "@/components/ui/toast";
import {
  PAGE_SHELL,
  CARD_SECTION,
  ACTION_BTN,
  GHOST_BTN,
  SIGNATURE_PILL,
  SIGNATURE_STATUS_LABEL,
} from "@/lib/ui-tokens";
import {
  SignatureViewer,
  useSignatureViewer,
} from "@/components/modals/SignatureViewer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
export const Route = createFileRoute("/_app/signatures/$")({
  component: SignatureDetailPage,
});

// ── Mock Timeline ────────────────────────────────────────────────────────────

const TIMELINE_DOT: Record<string, string> = {
  signature_completed: "bg-(--green) border-(--green)",
  signature_requested: "bg-(--border) border-(--border)",
  signature_declined: "bg-(--warn) border-(--warn)",
  success: "bg-(--green) border-(--green)",
  neutral: "bg-(--border) border-(--border)",
};

interface PlacedField {
  id: string;
  fieldType: "signature" | "initials" | "date" | "text";
  pageNumber: number;
  xPercent: number;
  yPercent: number;
  widthPercent: number;
  heightPercent: number;
  signerRole: string;
}

// ── Custom SVGs ──────────────────────────────────────────────────────────────

function PenIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 14 14"
      fill="none"
      className="shrink-0"
    >
      <path
        d="M1.5 12h11M3 10.5l6-6-2.5-2.5-6 6V10.5h2.5z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="square"
      />
      <path
        d="M12.5 4.5l-3-3"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="square"
      />
    </svg>
  );
}

function InitialsIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 14 14"
      fill="none"
      className="shrink-0"
    >
      <path
        d="M2 3h4M4 3v8M8 3h4M8 11h4M10 3v8"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="square"
      />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 14 14"
      fill="none"
      className="shrink-0"
    >
      <rect
        x="2"
        y="2"
        width="10"
        height="10"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path
        d="M2 5h10M4 1v2M10 1v2"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="square"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 14 14"
      fill="none"
      className="hover:text-red-500 transition-colors"
    >
      <path
        d="M4 4l6 6M10 4l-6 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="square"
      />
    </svg>
  );
}

// ── Visual Field Planner Component ───────────────────────────────────────────

const PdfPageRenderer = memo(function PdfPageRenderer({
  pageNum,
  numPages,
  doc,
  fields,
  onPageClick,
  onDragStart,
  onDeleteField,
}: {
  pageNum: number;
  numPages: number;
  doc: any;
  fields: PlacedField[];
  onPageClick: (e: React.MouseEvent<HTMLDivElement>, pageNum: number) => void;
  onDragStart: (
    e: React.MouseEvent,
    fieldId: string,
    container: HTMLDivElement,
  ) => void;
  onDeleteField: (id: string, e: React.MouseEvent) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let renderTask: any = null;
    let isCancelled = false;

    const renderPage = async () => {
      if (!doc) return;
      const page = await doc.getPage(pageNum);
      if (isCancelled) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext("2d");
      if (!context) return;

      const viewport = page.getViewport({ scale: 1.2 });
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      renderTask = page.render({
        canvasContext: context,
        viewport: viewport,
      });

      try {
        await renderTask.promise;
      } catch (err: any) {
        if (err.name !== "RenderingCancelledException") {
          console.error("PDF render error:", err);
        }
      }
    };

    renderPage();

    return () => {
      isCancelled = true;
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [doc, pageNum]);

  return (
    <div className="flex flex-col items-center gap-2 mb-8 select-none">
      <span className="text-[10px] font-semibold text-muted-foreground uppercase">
        Page {pageNum} of {numPages}
      </span>
      <div
        ref={pageContainerRef}
        onClick={(e) => onPageClick(e, pageNum)}
        className="relative border border-(--border) bg-white cursor-crosshair shadow-sm"
        style={{
          width: "100%",
          maxWidth: "600px",
          aspectRatio: "8.5 / 11",
          position: "relative",
        }}
      >
        <canvas ref={canvasRef} className="w-full h-full block" />

        {fields
          .filter((f) => f.pageNumber === pageNum)
          .map((f) => (
            <div
              key={f.id}
              onMouseDown={(e) => {
                if (pageContainerRef.current) {
                  onDragStart(e, f.id, pageContainerRef.current);
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="absolute border border-(--brand) bg-(--brand-dim) flex items-center justify-between px-2 cursor-move text-[9px] font-bold text-(--brand) transition-shadow select-none group"
              style={{
                left: `${f.xPercent}%`,
                top: `${f.yPercent}%`,
                width: `${f.widthPercent}%`,
                height: `${f.heightPercent}%`,
                boxShadow: "0 2px 6px rgba(17, 76, 32, 0.12)",
              }}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                {f.fieldType === "signature" && <PenIcon />}
                {f.fieldType === "initials" && <InitialsIcon />}
                {f.fieldType === "date" && <CalendarIcon />}
                <span className="truncate uppercase tracking-wider text-[8px]">
                  {f.fieldType}
                </span>
              </div>
              <button
                onClick={(e) => onDeleteField(f.id, e)}
                className="text-(--brand) opacity-60 hover:opacity-100 flex items-center justify-center p-0.5"
              >
                <TrashIcon />
              </button>
            </div>
          ))}
      </div>
    </div>
  );
});

function VisualFieldPlanner({
  documentId,
  pdfUrl,
  initialFields = [],
  onClose,
  onSave,
}: {
  documentId: string;
  pdfUrl: string;
  initialFields: PlacedField[];
  onClose: () => void;
  onSave: (fields: PlacedField[]) => Promise<void>;
}) {
  const [fields, setFields] = useState<PlacedField[]>(initialFields);
  const [activeTool, setActiveTool] = useState<
    "signature" | "initials" | "date"
  >("signature");
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const justDraggedRef = useRef(false);

  // Load PDF.js from CDN dynamically
  useEffect(() => {
    const loadPdfjs = async () => {
      if (!(window as any).pdfjsLib) {
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js";
        document.body.appendChild(script);

        await new Promise((resolve) => {
          script.onload = resolve;
        });
      }

      const pdfjs = (window as any).pdfjsLib;
      pdfjs.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js";

      try {
        const doc = await pdfjs.getDocument({
          url: pdfUrl,
          withCredentials: true,
        }).promise;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
      } catch (err) {
        console.error("Error loading PDF via canvas renderer:", err);
        toast.error("Failed to load PDF preview");
      } finally {
        setLoadingPdf(false);
      }
    };

    loadPdfjs();
  }, [pdfUrl]);

  const handlePageClick = (
    e: React.MouseEvent<HTMLDivElement>,
    pageNum: number,
  ) => {
    if (justDraggedRef.current) {
      return;
    }
    const pageContainer = e.currentTarget;
    const rect = pageContainer.getBoundingClientRect();

    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const xPercent = (clickX / rect.width) * 100;
    const yPercent = (clickY / rect.height) * 100;

    let w = 24; // Default percentage width
    let h = 6; // Default percentage height
    if (activeTool === "initials") {
      w = 12;
      h = 5;
    } else if (activeTool === "date") {
      w = 18;
      h = 5;
    }

    // Anchor field centered around click
    const newField: PlacedField = {
      id: `field-${Math.random().toString(36).substring(5)}`,
      fieldType: activeTool,
      pageNumber: pageNum,
      xPercent: Math.max(0, Math.min(100 - w, xPercent - w / 2)),
      yPercent: Math.max(0, Math.min(100 - h, yPercent - h / 2)),
      widthPercent: w,
      heightPercent: h,
      signerRole: "primary",
    };

    setFields([...fields, newField]);
  };

  const handleDragStart = (
    e: React.MouseEvent,
    fieldId: string,
    pageContainer: HTMLDivElement,
  ) => {
    e.stopPropagation();
    e.preventDefault();

    const pageRect = pageContainer.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;

    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    const startLeft = (field.xPercent / 100) * pageRect.width;
    const startTop = (field.yPercent / 100) * pageRect.height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const newLeft = startLeft + deltaX;
      const newTop = startTop + deltaY;

      const xPercent = Math.max(
        0,
        Math.min(100 - field.widthPercent, (newLeft / pageRect.width) * 100),
      );
      const yPercent = Math.max(
        0,
        Math.min(100 - field.heightPercent, (newTop / pageRect.height) * 100),
      );

      setFields((prev) =>
        prev.map((f) => (f.id === fieldId ? { ...f, xPercent, yPercent } : f)),
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);

      // Prevent click event immediately following a drag
      justDraggedRef.current = true;
      setTimeout(() => {
        justDraggedRef.current = false;
      }, 50);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const deleteField = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFields(fields.filter((f) => f.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(fields);
      toast.success("Signature fields saved successfully");
      onClose();
    } catch (err: any) {
      toast.error(err.message || "Failed to save fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] bg-card border border-(--border)">
      {/* Visual Canvas Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-(--border) px-4 py-3 gap-3 bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <button
            onClick={onClose}
            className="text-[11px] font-bold text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors shrink-0"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M6 2L3 5l3 3"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="square"
              />
            </svg>
            Back
          </button>
          <div className="h-4 w-px bg-(--border) shrink-0" />

          {/* Tool selector */}
          <div className="flex border border-(--border) bg-card overflow-hidden shrink-0">
            <button
              onClick={() => setActiveTool("signature")}
              className={`px-2.5 py-1.5 sm:px-3 text-[10px] font-bold flex items-center gap-1.5 border-r border-(--border) transition-colors ${activeTool === "signature" ? "bg-(--brand) text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              <PenIcon />
              <span className="hidden sm:inline">Signature</span>
              <span className="inline sm:hidden">Sign</span>
            </button>
            <button
              onClick={() => setActiveTool("initials")}
              className={`px-2.5 py-1.5 sm:px-3 text-[10px] font-bold flex items-center gap-1.5 border-r border-(--border) transition-colors ${activeTool === "initials" ? "bg-(--brand) text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              <InitialsIcon />
              Initials
            </button>
            <button
              onClick={() => setActiveTool("date")}
              className={`px-2.5 py-1.5 sm:px-3 text-[10px] font-bold flex items-center gap-1.5 transition-colors ${activeTool === "date" ? "bg-(--brand) text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              <CalendarIcon />
              <span className="hidden sm:inline">Date Signed</span>
              <span className="inline sm:hidden">Date</span>
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between md:justify-end gap-3 w-full md:w-auto mt-1 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-(--border)">
          <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider shrink-0">
            {fields.length} blocks placed
          </span>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`${ACTION_BTN} disabled:opacity-50 text-[10.5px] px-3.5 h-8`}
          >
            {saving ? "Saving..." : "Save Placed Fields"}
          </button>
        </div>
      </div>

      {/* Editor Body */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto p-8 bg-stone-50/50 flex flex-col items-center"
      >
        {loadingPdf ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <svg
              className="animate-spin text-(--brand)"
              width="20"
              height="20"
              viewBox="0 0 12 12"
              fill="none"
            >
              <circle
                cx="6"
                cy="6"
                r="4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeDasharray="14"
                strokeDashoffset="5"
              />
            </svg>
            <p className="text-[11px] text-muted-foreground font-semibold">
              Loading document preview...
            </p>
          </div>
        ) : (
          Array.from({ length: numPages }).map((_, i) => (
            <PdfPageRenderer
              key={i + 1}
              pageNum={i + 1}
              numPages={numPages}
              doc={pdfDoc}
              fields={fields}
              onPageClick={handlePageClick}
              onDragStart={handleDragStart}
              onDeleteField={deleteField}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Signature Detail Page ─────────────────────────────────────────────────────

function SignatureDetailPage() {
  const params = useParams({ from: "/_app/signatures/$" });
  const signatureId = params._splat ?? "";
  const session = getSession();
  const workspaceId = session?.workspaceId ?? "";
  const navigate = useNavigate();
  const [isPreparing, setIsPreparing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const {
    sig: viewerSig,
    open: openSig,
    close: closeSig,
  } = useSignatureViewer();
  const sendSig = trpc.signatures.send.useMutation();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("prepare") === "true") {
      setIsPreparing(true);
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const sig_q = trpc.signatures.get.useQuery(
    { id: signatureId },
    { enabled: Boolean(workspaceId && signatureId) },
  );

  const nudge_m = trpc.signatures.nudge.useMutation({
    onSuccess() {
      toast.success("Reminder sent successfully!");
    },
    onError(err) {
      toast.error(err.message || "Failed to send reminder");
    },
  });

  const saveFields_m = trpc.signatures.saveFields.useMutation();

  const utils = trpc.useUtils();

  const deleteSignature = trpc.signatures.delete.useMutation({
    onSuccess() {
      toast.success("Signature request deleted");
      navigate({ to: "/signatures" });
      utils.signatures.list.invalidate();
    },
    onError(err) {
      toast.error(err.message || "Failed to delete signature request");
    },
  });

  const resetSignature = trpc.signatures.reset.useMutation({
    onSuccess() {
      toast.success("Signature reset to pending successfully!");
      sig_q.refetch();
      utils.signatures.list.invalidate();
    },
    onError(err) {
      toast.error(err.message || "Failed to reset signature");
    },
  });

  const isLoading = sig_q.isLoading;
  const data = sig_q.data;
  const s = data?.signature;
  const clientName = data?.clientName;
  const clientId = s?.clientId;
  const documentName = data?.documentName;
  const documentMimeType = data?.documentMimeType;
  const initialFields = (data?.fields ?? []) as PlacedField[];
  const events = data?.events ?? [];

  if (isLoading) {
    return (
      <div className={PAGE_SHELL}>
        <Skeleton className="h-3.5 w-20" />
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-6 w-64" />
            <Skeleton className="h-3.5 w-48" />
          </div>
          <Skeleton className="h-8 w-28" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="border border-(--border) bg-card px-3.5 py-3 space-y-2"
            >
              <Skeleton className="h-2.5 w-16" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
        <div className={CARD_SECTION}>
          <div className="px-4 py-2.5 border-b border-(--border)">
            <Skeleton className="h-3 w-24" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="px-4 py-3 flex gap-3">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (!s) return <div className={PAGE_SHELL}>No signature found</div>;

  const pdfDownloadUrl = `${runtimeConfig.apiUrl || (import.meta.env.DEV ? "http://localhost:8787" : window.location.origin)}/upload/download/${s.documentId}`;

  // Render Visual Field Planner Overlay
  if (isPreparing) {
    return (
      <div className={PAGE_SHELL}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-[18px] font-bold">Prepare Signature Fields</h1>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Place click-to-sign boxes precisely onto the document layout.
            </p>
          </div>
        </div>
        <VisualFieldPlanner
          documentId={s.documentId}
          pdfUrl={pdfDownloadUrl}
          initialFields={initialFields}
          onClose={() => {
            setIsPreparing(false);
            sig_q.refetch();
          }}
          onSave={async (fields) => {
            await saveFields_m.mutateAsync({
              documentId: s.documentId,
              fields: fields.map((f) => ({
                fieldType: f.fieldType,
                pageNumber: f.pageNumber,
                xPercent: f.xPercent,
                yPercent: f.yPercent,
                widthPercent: f.widthPercent,
                heightPercent: f.heightPercent,
                signerRole: f.signerRole,
              })),
            });
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className={PAGE_SHELL}>
        {/* Back link */}
        <Link
          to="/signatures"
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M6 2L3 5l3 3"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
          </svg>
          All signatures
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <h1 className="text-[18px] font-bold tracking-tight text-foreground leading-tight truncate">
                {documentName}
              </h1>
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                <Link
                  to={`/clients/$clientId`}
                  params={{ clientId: String(clientId) }}
                  search={{ tab: "overview" }}
                  className="hover:text-foreground transition-colors underline underline-offset-2"
                >
                  {clientName}
                </Link>
                · Requested {new Date(s.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <span
              className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 ${SIGNATURE_PILL[s.status]}`}
            >
              {SIGNATURE_STATUS_LABEL[s.status]}
            </span>
            {s.status === "pending" && (
              <>
                <button
                  onClick={() => setIsPreparing(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold bg-(--brand-dim) text-(--brand) border border-(--brand-border) hover:bg-(--brand) hover:text-white transition-colors"
                >
                  <PenIcon />
                  Prepare fields
                </button>
                {data.hasBeenSent ? (
                  <button
                    className={GHOST_BTN}
                    onClick={() => nudge_m.mutate({ id: s.id })}
                    disabled={nudge_m.isPending}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M11 1L1 5.5l4 1.5L7 11l4-10z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="square"
                        strokeLinejoin="miter"
                      />
                    </svg>
                    {nudge_m.isPending ? "Sending..." : "Send reminder"}
                  </button>
                ) : (
                  <button
                    className={ACTION_BTN}
                    onClick={async () => {
                      try {
                        await sendSig.mutateAsync({
                          signatureId: s.id,
                        });
                        toast.success("Signature request sent successfully!");
                        sig_q.refetch();
                        utils.signatures.list.invalidate();
                      } catch (err: any) {
                        toast.error(
                          err.message || "Failed to send signature request",
                        );
                      }
                    }}
                    disabled={sendSig.isPending}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M11 1L1 5.5l4 1.5L7 11l4-10z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="square"
                        strokeLinejoin="miter"
                      />
                    </svg>
                    {sendSig.isPending ? "Sending..." : "Send request"}
                  </button>
                )}
              </>
            )}

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 h-8 text-[11px] font-bold text-(--warn) hover:bg-(--colors-red-50) transition-colors border border-transparent hover:border-(--colors-red-200)"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2 3h8M3 3v8h6V3M4 3V1.5h4V3M4.5 5.5v3M7.5 5.5v3"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
              </svg>
              Delete
            </button>
          </div>
        </div>

        <AlertDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
        >
          <AlertDialogContent className="rounded-none border-2 border-(--border) max-w-[400px]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[16px] font-bold tracking-tight">
                Delete signature request?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[12px] leading-relaxed">
                Are you sure you want to delete this signature request? This
                will permanently remove the signature history and all mapped
                interactive fields. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6">
              <AlertDialogCancel className="rounded-none h-9 text-[12px] font-bold">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteSignature.mutate({ id: signatureId })}
                className="rounded-none h-9 text-[12px] font-bold bg-(--warn) te hover:bg-(--warn)/90 border-none"
              >
                Delete request
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
          <AlertDialogContent className="rounded-none border-2 border-(--border) max-w-[400px]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[16px] font-bold tracking-tight">
                Reset signature request?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[12px] leading-relaxed">
                Are you sure you want to reset this signature request to
                pending? Any existing signature image/data, timestamp, and IP
                address will be permanently cleared, allowing the client to sign
                again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6">
              <AlertDialogCancel className="rounded-none h-9 text-[12px] font-bold">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => resetSignature.mutate({ id: signatureId })}
                className="rounded-none h-9 text-[12px] font-bold bg-(--brand) text-white hover:bg-(--brand)/90 border-none"
              >
                Reset to pending
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Draft banner */}
        {!data.hasBeenSent && s.status === "pending" && (
          <div className="flex items-start gap-2.5 px-4 py-3 bg-(--brand-dim) border border-(--brand-border) text-(--brand) mb-4">
            <svg
              className="shrink-0 mt-0.5"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <circle
                cx="7"
                cy="7"
                r="6"
                stroke="currentColor"
                strokeWidth="1.2"
              />
              <path
                d="M7 4v3.5M7 9h.01"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <div>
              <p className="text-[11px] font-bold">Draft Signature Request</p>
              <p className="text-[10px] text-muted-foreground font-normal mt-0.5">
                This signature request is saved as a draft. Click 'Prepare
                fields' to place signing elements, then click 'Send request' to
                notify the client and activate the link.
              </p>
              {(() => {
                try {
                  const channels: string[] = (s as any).deliveryChannels
                    ? JSON.parse((s as any).deliveryChannels)
                    : ["email"];
                  if (channels && channels.length > 0) {
                    return (
                      <p className="text-[9.5px] font-bold text-(--brand) mt-2 uppercase tracking-[0.06em]">
                        Delivery channels: {channels.join(", ")}
                      </p>
                    );
                  }
                } catch (e) {}
                return null;
              })()}
            </div>
          </div>
        )}

        {/* Meta strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <div className="border border-(--border) bg-card px-3.5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
              Status
            </p>
            <p className="text-[13px] font-bold mt-1 text-foreground capitalize">
              {s.status}
            </p>
          </div>
          <div className="border border-(--border) bg-card px-3.5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
              Requested
            </p>
            <p className="text-[13px] font-bold mt-1 text-foreground">
              {new Date(s.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="border border-(--border) bg-card px-3.5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
              Signed at
            </p>
            <p className="text-[13px] font-bold mt-1 text-foreground">
              {s.signedAt ? new Date(s.signedAt).toLocaleDateString() : "—"}
            </p>
          </div>
          <div className="border border-(--border) bg-card px-3.5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
              IP address
            </p>
            <p className="text-[13px] font-bold mt-1 text-foreground font-mono">
              {s.ipAddress ?? "—"}
            </p>
          </div>
        </div>

        {/* Document preview card */}
        <div className={CARD_SECTION}>
          <div className="px-4 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-foreground">
              Document
            </p>
          </div>
          <div className="flex items-center gap-3 px-4 py-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center border border-(--border) bg-(--muted-token)">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect
                  x="3"
                  y="1"
                  width="14"
                  height="18"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
                <path
                  d="M6 6h8M6 9h8M6 12h5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="square"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-foreground truncate">
                {documentName}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                PDF ·{" "}
                {initialFields.length > 0
                  ? `${initialFields.length} interactive signature blocks mapped`
                  : "No interactive fields mapped yet"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                openSig({
                  id: s.id,
                  documentId: s.documentId,
                  documentMimeType,
                  signatureData: s.signatureData,
                  signatureType: s.signatureType,
                  clientName,
                  documentName,
                  signedAt: s.signedAt,
                  ipAddress: s.ipAddress,
                });
              }}
              className={GHOST_BTN}
            >
              {/*<svg width="20" height="20" viewBox="0 0 13 13" fill="none">
                <path
                  d="M1.5 6.5C3.125 3.25 9.875 3.25 11.5 6.5C9.875 9.75 3.125 9.75 1.5 6.5Z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="square"
                />
                <circle cx="6.5" cy="6.5" r="1.5" fill="currentColor" />
              </svg>*/}
              View
            </button>
          </div>

          {/* Signature status banner */}
          {s.status === "signed" && (
            <div className="mx-4 mb-4 flex items-center gap-2.5 px-3 py-2.5 bg-(--green-bg) border border-(--colors-green-200)">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M3 7l3 3 5-5"
                  stroke="var(--green)"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
              </svg>
              <div>
                <p className="text-[11px] font-semibold text-(--green)">
                  Signed
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {s.signedAt ? new Date(s.signedAt).toLocaleString() : ""} · IP{" "}
                  {s.ipAddress}
                </p>
              </div>
            </div>
          )}

          {s.status === "declined" && (
            <div className="mx-4 mb-4 flex items-center gap-2.5 px-3 py-2.5 bg-(--colors-red-50) border border-(--colors-red-200)">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M4 4l6 6M10 4l-6 6"
                  stroke="var(--warn)"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                />
              </svg>
              <div>
                <p className="text-[11px] font-semibold text-(--warn)">
                  Declined
                </p>
                <p className="text-[10px] text-muted-foreground">
                  The client declined to sign this document
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className={CARD_SECTION}>
          <div className="px-4 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-foreground">
              Activity
            </p>
          </div>
          <div className="px-4 py-3">
            <div className="relative ml-2.5">
              <div className="absolute left-0 top-1 bottom-1 w-px bg-(--border)" />
              <div className="space-y-4">
                {events.map((ev) => (
                  <div
                    key={ev.id}
                    className="flex items-start gap-3 relative pl-5"
                  >
                    <div
                      className={`absolute left-[-3px] top-1.5 w-[7px] h-[7px] border ${TIMELINE_DOT[ev.eventType] ?? TIMELINE_DOT.neutral}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-foreground">
                        {ev.eventType.replace("_", " ").toUpperCase()}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {ev.payload
                          ? JSON.parse(ev.payload).title || ev.payload
                          : ""}
                      </p>
                    </div>
                    <span className="text-[9.5px] text-muted-foreground/60 shrink-0 whitespace-nowrap">
                      {new Date(ev.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <SignatureViewer sig={viewerSig} onClose={closeSig} />
    </>
  );
}

{
  /*{(s.status === "signed" || s.status === "declined") && (
  <button
    onClick={() => setShowResetConfirm(true)}
    className={GHOST_BTN}
  >
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M2 2h8v8H2V2zM2 5h8M2 8h8"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
    Reset to pending
  </button>
)}*/
}
